import type {ReactElement} from 'react';
import {useState, useRef, useEffect} from 'react';
import {SearchBar, SearchBarSize} from '@Components/search-bar';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ControlledList} from '@Components/controlled-list';
import {timezoneDropdownId} from '@Components/event-wizard/timezone/timezone.types';
import type {ControlledListItem3} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import type {TimeZone} from 'timezones-list';
import {onEventWizardTimeZoneUpdate} from '@Components/event-wizard/event-wizard-slice';
import {updatePreviewCountdown, updatePreviewDateTime, updatePreviewTimeZone} from '@Components/event-wizard/preview/preview.helper';
import {useDispatch} from 'react-redux';
import {filterTimeZones} from '@Components/event-wizard/timezone/timezone.helper';
import {
  useEventWizardCountdown,
  useEventWizardEndDateTime,
  useEventWizardRecurringData,
  useEventWizardStartDateTime,
  useEventWizardTimeZone,
} from '@Components/event-wizard/event-wizard.hooks';
import {getGMTLabel, getTimezoneDisplayLabel} from '@Components/event-wizard/timezone-content/timezone-content.helper';
import {SkeletonLoader} from '@Components/skeleton-loader';
import {SkeletonLoaderVariant} from '@Components/skeleton-loader/skeleton-loader.types';
import styles from './timezone-content.module.scss';
import type {TimezoneContentProps} from './timezone-content.types';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';

export function TimezoneContent({onSelectTimezone}: TimezoneContentProps): ReactElement<any> {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isListReady, setIsListReady] = useState<boolean>(false);

  const dispatch = useDispatch();
  const selectedStartDateTime = useEventWizardStartDateTime();
  const selectedEndDateTime = useEventWizardEndDateTime();
  const recurringPattern = useEventWizardRecurringData();
  const selectedTimezone: TimeZone = useEventWizardTimeZone();
  const filteredTimeZones: TimeZone[] = filterTimeZones(searchTerm);
  const isShowCountdown = useEventWizardCountdown();

  const popupRef = useRef<HTMLDivElement | null>(null);
  const {topShadow} = useTopBottomScrollShadow(popupRef);

  useEffect(() => {
    const rafID = requestAnimationFrame(() => {
      setIsListReady(true);
    });

    return (): void => {
      cancelAnimationFrame(rafID);
    }
  }, []);

  const updateTimeZone = (newTimeZone: TimeZone, wasSelected: boolean): void => {
    dispatch(onEventWizardTimeZoneUpdate(newTimeZone));
    updatePreviewCountdown(isShowCountdown ? selectedStartDateTime : undefined, newTimeZone);
    updatePreviewTimeZone(newTimeZone.tzCode);
    updatePreviewDateTime({
      startDateTime: selectedStartDateTime,
      endDateTime: selectedEndDateTime,
      recurringPattern: recurringPattern,
      timeZone: newTimeZone,
    });

    if (onSelectTimezone) {
      onSelectTimezone(newTimeZone, wasSelected);
    }
  };

  const getContentItems = (): ControlledListItem3[] => {
    return filteredTimeZones.map((timeZone) => {
      const isSelected = selectedTimezone.tzCode === timeZone.tzCode;

      return {
        id: timeZone.tzCode,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: getContentItemText(timeZone, isSelected),
        containsDangerouslySetText: true,
        textContainerClass: styles.listItemTextContainer,
        isSelected,
        showSelectionCheckmark: false,
        className: styles.listItem,
        textClass: styles.listItemText,
        onClick(): void {
          updateTimeZone(timeZone, isSelected);
        },
      };
    });
  };

  const getContentItemText = (timeZone: TimeZone, isSelected: boolean): string => {
    return `
      <div class="flex-row-align-center justify-content-between _full-width">
        <div class="flex-1 spacing-m-r-2 body-md-regular ${isSelected ? 'content-primary body-md-bold' : 'content-body'}">${getTimezoneDisplayLabel(timeZone, false)}</div>
        <div class="${styles.gmtLabel} body-md-regular ${isSelected ? 'content-primary body-md-bold' : 'content-body'}">${getGMTLabel(timeZone)}</div>
      </div>
    `
  }

  return (
    <div ref={popupRef} className={styles.contentContainer}>
      <SearchBar
        className={`${styles.searchBar} ${topShadow ? styles.searchScroll : styles.searchNoScroll} bg-neutral-0 spacing-m-b-2`}
        placeholder={window.i18next.t('pmwjs_search')}
        size={SearchBarSize.MEDIUM}
        iconClass={styles.searchIcon}
        iconSize={IconSize.SIZE_ICON_16}
        onInput={setSearchTerm}
        autoFocus
        isSticky
      />
      {isListReady ? (
        <ControlledList scrollIntoViewIfSelected id={timezoneDropdownId} items={getContentItems()} />
      ) : (
        <SkeletonLoader
          className="spacing-m-t-4"
          variant={SkeletonLoaderVariant.GRID}
          isPulsating
          options={{columns: 1, gridItemHeight: '45px', gap: 20}}
        />
      )}
    </div>
  );
}
